import { Fragment, type ReactNode } from 'react';
import './Markdown.scss';

interface MarkdownProps {
  /** Raw markdown text (controlled LLM narrative output). */
  children?: string;
  /** Optional extra class on the wrapper. */
  className?: string;
}

/** Inline token patterns, applied in priority order over a text run. */
const INLINE_RULES: { regex: RegExp; render: (match: RegExpMatchArray, key: string) => ReactNode }[] = [
  // Bold: **text** or __text__
  { regex: /\*\*([^*]+)\*\*|__([^_]+)__/, render: (m, key) => <strong key={key}>{m[1] ?? m[2]}</strong> },
  // Italic: *text* or _text_
  { regex: /\*([^*]+)\*|_([^_]+)_/, render: (m, key) => <em key={key}>{m[1] ?? m[2]}</em> },
  // Inline code: `code`
  { regex: /`([^`]+)`/, render: (m, key) => <code key={key} className="md-code">{m[1]}</code> },
];

/**
 * Render inline markdown (bold / italic / code) inside a single text run into
 * an array of React nodes. Scans for the earliest-matching inline token, emits
 * the preceding plain text, the styled token, then recurses on the remainder.
 *
 * @param text - A single line (or cell) of markdown text
 * @param keyPrefix - Stable key prefix so sibling nodes get unique keys
 * @returns React nodes with inline formatting applied
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let rest = text;
  let counter = 0;

  while (rest.length > 0) {
    let earliest: { index: number; length: number; node: ReactNode } | null = null;

    for (const rule of INLINE_RULES) {
      const match = rest.match(rule.regex);
      if (match && match.index !== undefined) {
        if (earliest === null || match.index < earliest.index) {
          earliest = {
            index: match.index,
            length: match[0].length,
            node: rule.render(match, `${keyPrefix}-i${counter}`),
          };
        }
      }
    }

    if (earliest === null) {
      nodes.push(rest);
      break;
    }

    if (earliest.index > 0) {
      nodes.push(<Fragment key={`${keyPrefix}-t${counter}`}>{rest.slice(0, earliest.index)}</Fragment>);
    }
    nodes.push(earliest.node);
    rest = rest.slice(earliest.index + earliest.length);
    counter += 1;
  }

  return nodes;
}

/** Split a GFM pipe-table row into trimmed cells (ignoring the outer pipes). */
function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());
}

/** True when a line is a GFM table separator, e.g. `| --- | :--: |`. */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(line);
}

/**
 * A minimal, dependency-free markdown renderer for the controlled narrative
 * summaries (headings, bold/italic/code, bullet + ordered lists, GFM pipe
 * tables, horizontal rules, and paragraphs). Deliberately not a full CommonMark
 * parser — it covers the structures the summarization reduce-step emits.
 *
 * @param children - Raw markdown string
 * @param className - Optional wrapper class
 * @returns The rendered markdown as styled React elements
 */
export function Markdown({ children, className }: MarkdownProps): JSX.Element {
  const source = (children ?? '').replace(/\r\n/g, '\n');
  const lines = source.split('\n');
  const blocks: ReactNode[] = [];

  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      i += 1;
      continue;
    }

    // Horizontal rule: ---, ***, ___
    if (/^([-*_])\1{2,}$/.test(trimmed)) {
      blocks.push(<hr key={`hr-${key++}`} className="md-hr" />);
      i += 1;
      continue;
    }

    // Heading: #..######
    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      blocks.push(
        <Tag key={`h-${key++}`} className={`md-h md-h${level}`}>
          {renderInline(heading[2], `h${key}`)}
        </Tag>,
      );
      i += 1;
      continue;
    }

    // GFM pipe table: header row + separator row + body rows.
    if (trimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(trimmed);
      const bodyRows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].trim().includes('|') && lines[j].trim() !== '') {
        bodyRows.push(splitRow(lines[j]));
        j += 1;
      }
      blocks.push(
        <div key={`tbl-${key++}`} className="md-table-wrapper">
          <table className="md-table">
            <thead>
              <tr>
                {header.map((cell, index) => (
                  <th key={index}>{renderInline(cell, `th-${key}-${index}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{renderInline(cell, `td-${key}-${rowIndex}-${cellIndex}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      i = j;
      continue;
    }

    // Unordered list: -, *, +
    if (/^[-*+]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].trim().replace(/^[-*+]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${key++}`} className="md-ul">
          {items.map((item, index) => (
            <li key={index}>{renderInline(item, `uli-${key}-${index}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Ordered list: 1. 2. 3.
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ol key={`ol-${key++}`} className="md-ol">
          {items.map((item, index) => (
            <li key={index}>{renderInline(item, `oli-${key}-${index}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Paragraph: gather consecutive non-blank, non-structural lines.
    const paragraph: string[] = [];
    while (i < lines.length) {
      const current = lines[i];
      const currentTrimmed = current.trim();
      if (
        currentTrimmed === '' ||
        /^(#{1,6})\s+/.test(currentTrimmed) ||
        /^[-*+]\s+/.test(currentTrimmed) ||
        /^\d+\.\s+/.test(currentTrimmed) ||
        /^([-*_])\1{2,}$/.test(currentTrimmed) ||
        (currentTrimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
      ) {
        break;
      }
      paragraph.push(currentTrimmed);
      i += 1;
    }
    blocks.push(
      <p key={`p-${key++}`} className="md-p">
        {renderInline(paragraph.join(' '), `p${key}`)}
      </p>,
    );
  }

  return <div className={`md-root${className ? ` ${className}` : ''}`}>{blocks}</div>;
}
